// © Microsoft Corporation. All rights reserved.

import { ConfigReference, ExperienceConfig } from "@msnews/config-data-types";
import { getCacheKeyFromConfigRef, getChildConfigRefs, sendAppError } from "../../utilities/Utilities";

import { FallbackResolverAppErrors } from "../../utilities/ResolverAppErrors";
import { ResolverEnvironment } from "@msnews/core-contracts";
import { SingleConfigResolver } from "./SingleConfigResolver";

/**
 * Resolver class
 * This is a fallback scenarion when CRS is not available.
 */
export class Resolver {
    /**
     * Dictionary to maintain all resolved config for internal state management
     * @type {Map<string, ExperienceConfig>}
     */
    private mergedConfigMap: Map<string, ExperienceConfig> = new Map<string, ExperienceConfig>();

    /** Application Type */
    private appType: string;

    /** Resolver Environment */
    private resolverEnvironment: ResolverEnvironment;

    /** Single Config Resolver */
    private singleConfigResolver: SingleConfigResolver;

    /**
     * Initialize resolver based on fetching parameters.
     * @param {ResolverEnvironment} resolverEnvironment
     */
    constructor(resolverEnvironment: ResolverEnvironment) {
        if (!resolverEnvironment
            || !resolverEnvironment.configServiceParameters
            || !resolverEnvironment.appType
            || !resolverEnvironment.configServiceParameters.configServiceBaseUrl
            || !resolverEnvironment.targetScope) {
            throw new Error("Resolver.ts: ResolverEnvironment or configServiceParameters or targetScope does not exist.");
        }

        this.resolverEnvironment = resolverEnvironment;
        this.appType = resolverEnvironment.appType;
        this.singleConfigResolver = new SingleConfigResolver(resolverEnvironment);
    }

    /**
     * Gets all the config in the config tree in a recursive manner.
     * @param {ConfigReference} entity entity for the root config.
     * @param {boolean} getFullTree indicate whether we want to load entire tree at once.
     * @returns {Map<string, ExperienceConfig>} returns the merged config map.
     */
    public async getConfig(entity: ConfigReference, getFullTree = false): Promise<ExperienceConfig> {
        const cacheKey = getCacheKeyFromConfigRef(entity, this.appType);

        // If this is not in the map, get the tree recursively and add all child configs to map
        if (!this.mergedConfigMap.has(cacheKey)) {
            await this.getConfigRecursive(entity, getFullTree);

            const allNullFetches = this.singleConfigResolver.getFailedFetches();
            if (allNullFetches.length > 0) {
                allNullFetches.forEach((nullFetchUrl) => {
                    sendAppError(this.resolverEnvironment.appErrorCallback,
                        {
                            ...FallbackResolverAppErrors.BlobFetchReturnedNull,
                            message: "Fetch failed for a config",
                            pb: {
                                ...FallbackResolverAppErrors.BlobFetchReturnedNull.pb,
                                customMessage: `Failed fetch url: ${nullFetchUrl}`
                            }
                        });
                });
            }
        }

        return this.mergedConfigMap.get(cacheKey);
    }

    /**
     * Get configMap downloaded by fallback resolver.
     */
    public getConfigMap(): Map<string, ExperienceConfig> {
        return this.mergedConfigMap;
    }

    /**
     * Helper function for getConfig. Gets all the config in the config tree in a recursive manner.
     * @param {ConfigReference} entity entity for the root config.
     * @param {boolean} getFullTree indicate whether we want to load entire tree at once.
     */
    public async getConfigRecursive(entity: ConfigReference, getFullTree = false): Promise<void> {
        const cacheKey = getCacheKeyFromConfigRef(entity, this.appType);
        if (!cacheKey || this.mergedConfigMap.has(cacheKey)) {
            return;
        }

        try {
            // root of specific sub tree
            const mergedConfig = await this.singleConfigResolver.resolveConfigAsync(entity);
            // add to memory even if it is undefined because we don't want to fetch fail states multiple times
            this.mergedConfigMap.set(cacheKey, mergedConfig);

            // Only fetch rest of the tree if requested.
            if (getFullTree) {
                const unresolvedConfigRefs = getChildConfigRefs(mergedConfig);
                if (unresolvedConfigRefs.size == 0) {
                    return;
                }

                const configTasks = [];
                unresolvedConfigRefs.forEach(entity => {
                    configTasks.push (this.getConfigRecursive(entity, getFullTree));
                });

                // if a single config fails, it fails all, exists today as well
                await Promise.all(configTasks);
            }
        } catch (error) {
            sendAppError(this.resolverEnvironment.appErrorCallback,
                {
                    ...FallbackResolverAppErrors.RecursiveGetFailed,
                    message: "Exception in getConfig.",
                    pb: {
                        ...FallbackResolverAppErrors.RecursiveGetFailed.pb,
                        customMessage: `CacheKey: ${cacheKey} / Error: ${error}`
                    }
                });
        }
    }
}